import { default as React } from 'react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { CardActions } from '@material-ui/core';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  alertApiRef,
  configApiRef,
  errorApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';

import { WorkloadClient } from '@internal/workload-api-helper';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { SeverityEnum } from '../../../enums/SeverityEnum';
import { getMessage } from '../../../utils/getMessage';
import { CommonRolesDialog } from './CommonRolesEditDialog';
import { Account, Sso } from '../types';
import { isOkayResponse, isValueDefined } from '../utils/checks';
import { BaseEditIcon } from '../BaseEditIcon';

interface CommonRolesEditIconProps {
  entity: Entity;
  commonRoles: Sso | undefined;
  cardClass: string;
  cardContentClass: string;
}

export const EditCommonRolesIcon = ({
  entity,
  commonRoles,
  cardClass,
  cardContentClass,
}: CommonRolesEditIconProps) => {
  let accountObject: { [key: string]: string | undefined };

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [workloadData, setWorkloadData] = useState<Account>();
  const [requesterEmail, setRequesterEmail] = useState('');

  const configApi = useApi(configApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  const workloadClient = new WorkloadClient(configApi, oauthApi);

  const accountId: string | undefined =
    entity?.metadata?.annotations?.['amazonaws.com/account-id'];

  const identity = useApi(identityApiRef);

  const handleClickOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const patchResourceData = async ({
    workload,
    resourceName,
    payload,
  }: {
    workload?: string;
    resourceName?: string;
    payload: { [_: string]: Object };
  }) => {
    return workloadClient.apiFetch(
      `/workloads/workload/${workload}/resource/${resourceName}`,
      'PATCH',
      payload,
    );
  };

  const fetchWorkloadData = async () => {
    return await workloadClient.apiFetch(`workloads/${accountId}`);
  };

  useAsync(async (): Promise<Account> => {
    const [workloadDetails] = await Promise.all([fetchWorkloadData()]);

    const [details] = await Promise.all([workloadDetails.json()]);
    const { email } = await identity.getProfileInfo();
    if (email) {
      setRequesterEmail(email);
    }

    setWorkloadData(details);

    return details;
  }, []);

  const parseWorkloadObjectValues = (
    catalogLocationStringList: string[] | undefined,
  ): { [key: string]: string | undefined } => {
    const resourceFileName: string | undefined =
      catalogLocationStringList?.pop();
    const resourceName = resourceFileName?.split('.')[0];
    const workload = catalogLocationStringList?.pop();

    return {
      resourceName,
      workload,
    };
  };

  useEffect(() => {
    async function getRequesterEmail() {
      const profile = await identity.getProfileInfo();
      setRequesterEmail(profile.email || '');
    }
    getRequesterEmail();
  }, [identity]);

  const stopLoadingAndCloseModal = () => {
    setIsModalOpen(false);
  };

  const killProcessAndReportError = (e: any) => {
    stopLoadingAndCloseModal();
    errorApi.post(e);
  };

  const postWorkloadApply = async ({
    githubWorkloadAlias,
    githubFoundationManualApplyParamPayload,
  }: {
    githubWorkloadAlias: string | undefined;
    githubFoundationManualApplyParamPayload: any;
  }): Promise<Response> => {
    if (githubWorkloadAlias === undefined)
      return Promise.resolve(new Response());

    const url = `workloads/workload/${githubWorkloadAlias}/apply`;
    return await workloadClient.apiFetch(
      url,
      'POST',
      githubFoundationManualApplyParamPayload,
    );
  };

  const callPatchApi = async (updated_common_roles: {}) => {
    const payload: { [key: string]: Object } = {
      sso: updated_common_roles,
    };

    // let accountObject: { [key: string]: string | undefined } = {};

    const stripCatalogLocation = workloadData?.catalogLocation;
    const catalogLocationStringList = stripCatalogLocation?.split('/');
    if (catalogLocationStringList) {
      accountObject = parseWorkloadObjectValues(catalogLocationStringList);
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({
          failureToDo: 'find your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return false;
    }

    if (isValueDefined(accountObject?.workload)) {
      try {
        const patchResourceDataResponse = await patchResourceData({
          workload: accountObject.workload,
          resourceName: accountObject.resourceName,
          payload,
        });

        if (!isOkayResponse(patchResourceDataResponse)) {
          stopLoadingAndCloseModal();
          alertApi.post({
            message: getMessage({
              failureToDo: 'update your resource with the data provided',
            }).FAILURE,
            severity: SeverityEnum.ERROR,
          });
        } else {
          alertApi.post({
            message: getMessage({
              successMessage: 'Updating Common Roles information.',
            }).SUCCESS,
            severity: SeverityEnum.INFO,
          });

          await catalogApi.refreshEntity(stringifyEntityRef(entity));
        }
      } catch (e: any) {
        killProcessAndReportError(e);
      }
    }
    return Promise.resolve(true);
  };

  const [loading, setLoading] = React.useState(false);
  const [computeUserEnabled, setComputeUserEnabled] = React.useState(
    commonRoles?.compute_user_enabled,
  );
  const [dataScientistEnabled, setDataScientistEnabled] = React.useState(
    commonRoles?.data_scientist_enabled,
  );

  const [databaseUserEnabled, setDatabaseUserEnabled] = React.useState(
    commonRoles?.database_user_enabled,
  );

  const [s3BucketUserEnabled, setS3BucketUserEnabled] = React.useState(
    commonRoles?.s3_bucket_user_enabled,
  );

  const [featureFlagUserEnabled, setFeatureFlagUserEnabled] = React.useState(
    commonRoles?.feature_flag_user_enabled,
  );

  const updated_common_roles_config = useMemo(
    () => ({
      compute_user_enabled: computeUserEnabled || false,
      data_scientist_enabled: dataScientistEnabled || false,
      database_user_enabled: databaseUserEnabled || false,
      s3_bucket_user_enabled: s3BucketUserEnabled || false,
      feature_flag_user_enabled: featureFlagUserEnabled || false,
    }),
    [
      computeUserEnabled,
      dataScientistEnabled,
      databaseUserEnabled,
      s3BucketUserEnabled,
      featureFlagUserEnabled,
    ],
  );

  async function handleConfirm(e: any) {
    e.preventDefault();
    const common_roles_config = commonRoles as Sso;

    if (!_.isEqual(common_roles_config, updated_common_roles_config)) {
      setLoading(true);
      const res = await callPatchApi(updated_common_roles_config);
      if (res) {
        const githubFoundationManualApplyParamPayload = {
          workload_alias: accountObject.workload,
          requester: requesterEmail,
          reason: 'Updating common_roles configuration',
        };
        postWorkloadApply({
          githubWorkloadAlias: accountObject.workload,
          githubFoundationManualApplyParamPayload:
            githubFoundationManualApplyParamPayload,
        });
      }
    }
    setLoading(false);
    handleClose();
  }

  return (
    <CardActions>
      <BaseEditIcon
        workloadData={workloadData}
        requesterEmail={requesterEmail}
        handleClickOpen={handleClickOpen}
        isModalOpen={isModalOpen}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        disableConfirm={_.isEqual(commonRoles, updated_common_roles_config)}
        apiValidationPayload
        isLoading={loading}
        isLoadingEdit={loading}
        dialogTitle="edit common roles"
        settingName="Common Roles"
        allowOwnerEdit
        allowEngineerEdit
      >
        <CommonRolesDialog
          cardClass={cardClass}
          cardContentClass={cardContentClass}
          setComputeUserEnabled={setComputeUserEnabled}
          computeUserEnabled={computeUserEnabled}
          setDataScientistEnabled={setDataScientistEnabled}
          dataScientistEnabled={dataScientistEnabled}
          setDatabaseUserEnabled={setDatabaseUserEnabled}
          databaseUserEnabled={databaseUserEnabled}
          setS3BucketUserEnabled={setS3BucketUserEnabled}
          s3BucketUserEnabled={s3BucketUserEnabled}
          setFeatureFlagUserEnabled={setFeatureFlagUserEnabled}
          featureFlagUserEnabled={featureFlagUserEnabled}
        />
      </BaseEditIcon>
    </CardActions>
  );
};
